/**
 * useUploader — uploads captured frames to Azure Blob via SAS URLs.
 *
 * Handles: batch SAS URL fetching, parallel uploads (4 concurrent),
 * progress tracking, and scan completion.
 */

import { useState, useCallback, useRef } from 'react'
import { createScan, getUploadUrls, uploadFrame, markScanComplete, getScan } from '@services/scanApi'
import type { Scan } from '@app-types/scan'

interface UploadProgress {
  total: number
  uploaded: number
  failed: number
  percent: number
}

type UploadPhase = 'idle' | 'creating' | 'uploading' | 'completing' | 'polling' | 'done' | 'error'

const CONCURRENT_UPLOADS = 4
const POLL_INTERVAL_MS = 5000

export function useUploader() {
  const [phase, setPhase] = useState<UploadPhase>('idle')
  const [progress, setProgress] = useState<UploadProgress>({ total: 0, uploaded: 0, failed: 0, percent: 0 })
  const [scan, setScan] = useState<Scan | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<number | null>(null)

  // Stop status polling
  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  // Upload frames and track progress
  const upload = useCallback(
    async (frames: Blob[], shopId: string, sectionName: string, sectionId?: string) => {
      try {
        setError(null)
        setPhase('creating')

        // Use provided sectionId or generate from name
        const finalSectionId = sectionId || ('sec_' + sectionName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''))

        // Step 1: Create scan
        const newScan = await createScan({
          shop_id: shopId,
          section_id: finalSectionId,
          section_name: sectionName,
          frame_count: frames.length,
        })
        setScan(newScan)

        // Step 2: Get SAS upload URLs
        setPhase('uploading')
        const { urls } = await getUploadUrls(newScan.id, frames.length)

        // Step 3: Upload frames in parallel batches
        const total = frames.length
        let uploaded = 0
        let failed = 0
        setProgress({ total, uploaded: 0, failed: 0, percent: 0 })

        // Process in batches of CONCURRENT_UPLOADS
        for (let i = 0; i < urls.length; i += CONCURRENT_UPLOADS) {
          const batch = urls.slice(i, i + CONCURRENT_UPLOADS)
          const promises = batch.map(async (urlInfo, batchIdx) => {
            const frameIdx = i + batchIdx
            if (frameIdx >= frames.length) return
            try {
              await uploadFrame(urlInfo.upload_url, frames[frameIdx])
              uploaded++
            } catch {
              failed++
              console.error(`Failed to upload frame ${frameIdx + 1}`)
            }
            setProgress({ total, uploaded, failed, percent: Math.round(((uploaded + failed) / total) * 100) })
          })
          await Promise.all(promises)
        }

        // Step 4: Mark scan complete
        setPhase('completing')
        const completed = await markScanComplete(newScan.id)
        setScan(completed)

        // Step 5: Poll for processing completion
        setPhase('polling')
        pollRef.current = window.setInterval(async () => {
          try {
            const updated = await getScan(newScan.id)
            setScan(updated)
            if (updated.status === 'ready' || updated.status === 'failed') {
              stopPolling()
              setPhase('done')
            }
          } catch {
            // Keep polling on transient errors
          }
        }, POLL_INTERVAL_MS)
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Upload failed'
        setError(msg)
        setPhase('error')
      }
    },
    [stopPolling],
  )

  // Reset state
  const reset = useCallback(() => {
    stopPolling()
    setPhase('idle')
    setProgress({ total: 0, uploaded: 0, failed: 0, percent: 0 })
    setScan(null)
    setError(null)
  }, [stopPolling])

  return { phase, progress, scan, error, upload, reset }
}
